import { SocialProvider } from './account.types';
import {
  GOOGLE_CLIENT_ID,
  buildGoogleOAuthUrl,
  openCenteredPopup,
} from '../../../auth/google-oauth.utils';

const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_SSO_CLIENT_ID ?? '';
const APPLE_REDIRECT_URI =
  import.meta.env.VITE_APPLE_SSO_REDIRECT_URI ?? window.location.origin;

const getGoogleIdToken = (): Promise<string> => {
  if (!GOOGLE_CLIENT_ID) {
    return Promise.reject(new Error('Google Sign-In is not configured.'));
  }

  return new Promise<string>((resolve, reject) => {
    const popup = openCenteredPopup(buildGoogleOAuthUrl(), 'google-oauth');
    if (!popup) {
      reject(
        new Error('Popup was blocked. Please allow popups and try again.'),
      );
      return;
    }

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(pollTimer);
      if (popup.closed) {
        return;
      }
      popup.close();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'google-oauth-error') {
        cleanup();
        reject(new Error('Google sign-in failed. Please try again.'));
        return;
      }

      if (event.data?.type !== 'google-oauth-credential') {
        return;
      }

      cleanup();
      resolve(event.data.credential);
    };

    window.addEventListener('message', onMessage);

    const pollTimer = window.setInterval(() => {
      if (!popup.closed) {
        return;
      }
      cleanup();
      reject(new Error('Google sign-in was cancelled.'));
    }, 500);
  });
};

const getAppleIdToken = async (): Promise<string> => {
  if (!window.AppleID?.auth) {
    throw new Error('Apple Sign-In is not available. Please try again later.');
  }

  if (!APPLE_CLIENT_ID) {
    throw new Error('Apple Sign-In is not configured.');
  }

  window.AppleID.auth.init({
    clientId: APPLE_CLIENT_ID,
    scope: 'name email',
    redirectURI: APPLE_REDIRECT_URI,
    usePopup: true,
  });

  const response = await window.AppleID.auth.signIn();
  const idToken = response.authorization?.id_token;

  if (!idToken) {
    throw new Error('Apple sign-in failed. Please try again.');
  }

  return idToken;
};

export const getIdToken = (provider: SocialProvider): Promise<string> => {
  if (provider === 'google') {
    return getGoogleIdToken();
  }

  if (provider === 'apple') {
    return getAppleIdToken();
  }

  return Promise.reject(new Error('Unsupported provider.'));
};
